import { useState, useRef, useEffect } from "react";
import Spinner from "@/components/Spinner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Trash2, Plus, Loader2, Mic, Square, Camera, ScanLine, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { invalidateSWR } from "@/lib/swr";
import { todayISO } from "@/lib/utils";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n.jsx";
import BarcodeScanner from "@/components/BarcodeScanner";
import NutritionLabelContributor from "@/components/NutritionLabelContributor";
import ReviewPromptModal from "@/components/ReviewPromptModal";
import { Sparkles } from "lucide-react";
import { noteMealSaved, shouldPromptForReview } from "@/lib/reviewPrompt";
import { ensureAIConsent } from "@/lib/aiConsent";
import { ItemRow, TotalCol } from "@/components/MealItemEditor";
import { NotesPoints, splitPoints } from "@/components/MealViewParts";

// Older recipe logs were stored as ONE roll-up item — expand them into the
// recipe's ingredients (scaled to the logged calories) so they stay editable.
async function expandRecipeMeal(meal, lang) {
  const items = meal.items || [];
  if (!meal.recipe_id || items.length !== 1 || items[0].per_100g) return items;
  try {
    const { data } = await api.get(`/meals/recipe-items/${meal.recipe_id}`, { params: { lang } });
    const base = data.items || [];
    const per = base.reduce((a, i) => a + (i.calories || 0), 0);
    if (!base.length || per <= 0) return items;
    const k = (items[0].calories || per) / per;
    return base.map((i) => ({
      ...i,
      quantity_g: Math.round(i.quantity_g * k),
      calories: Math.round(i.calories * k),
      protein_g: Math.round(i.protein_g * k * 10) / 10,
      carbs_g: Math.round(i.carbs_g * k * 10) / 10,
      fat_g: Math.round(i.fat_g * k * 10) / 10,
    }));
  } catch {
    return items;
  }
}

export default function AddMeal() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("loading"); // loading | describe | photo | review (multi-item incl manual) | barcode_scanning
  // Remembers WHICH picker screen we landed the review from (photo, label
  // or barcode). The header's back button uses it to return to the picker
  // instead of ejecting to Home — critical UX so users can retry a fresh
  // photo without re-selecting the meal type from the dashboard sheet.
  const [prevMode, setPrevMode] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [description, setDescription] = useState("");
  const [items, setItems] = useState([]);
  const [mealName, setMealName] = useState("");
  const [mealType, setMealType] = useState("lunch");
  const [notes, setNotes] = useState("");
  const [confidence, setConfidence] = useState(1);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  // Separate ref for the gallery / photo-library picker. Same file-picker
  // element as the camera trigger except WITHOUT `capture="environment"`,
  // so tapping it opens the OS photo library on both iOS (Photos app) and
  // Android (Gallery/Files picker) instead of jumping straight to the
  // camera. Cross-platform via plain HTML — no Capacitor plugin needed.
  const galleryRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  // "Complete this product" flow — surfaced when a scanned barcode returns
  // from OpenFoodFacts with missing macros. Users can snap the nutrition
  // label so GPT-4o Vision fills in the blanks and we mirror back to OFF.
  const [contributor, setContributor] = useState(null); // null | { barcode, suggestedName, isLiquid, itemIndex }

  const startRecording = async () => {
    if (recording || transcribing) return;
    // Apple 5.1.1 / 5.1.2 — explicit consent before voice → OpenAI Whisper.
    const ok = await ensureAIConsent(lang);
    if (!ok) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(lang === "en" ? "Browser doesn't support voice recording" : "المتصفح لا يدعم التسجيل الصوتي");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 800) { toast.error(lang === "en" ? "Recording too short" : "التسجيل قصير جداً"); setTranscribing(false); return; }
        const ext = (mr.mimeType || "").includes("mp4") ? "mp4" : "webm";
        const form = new FormData();
        form.append("file", blob, `voice.${ext}`);
        form.append("language", lang);
        setTranscribing(true);
        try {
          const { data } = await api.post("/coach/transcribe", form);
          const text = (data.text || "").trim();
          if (!text) toast.error(lang === "en" ? "No speech detected" : "لم يتم التعرف على أي كلام");
          else setDescription((prev) => (prev ? prev + " " + text : text));
        } catch {
          toast.error(lang === "en" ? "Couldn't transcribe audio" : "تعذر تحويل الصوت إلى نص");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      // Distinguish permission-denied from other errors so we can point
      // the user to Settings instead of a generic message. On iOS the
      // OS returns NotAllowedError when NSMicrophoneUsageDescription is
      // missing OR when the user tapped "Don't Allow"; on Android when
      // the app-level RECORD_AUDIO permission is denied.
      const isDenied = e && (e.name === "NotAllowedError" || e.name === "PermissionDeniedError" || e.name === "SecurityError");
      if (isDenied) {
        const { openAppSettings } = await import("@/lib/openAppSettings");
        toast.error(
          lang === "en" ? "Microphone blocked" : "الميكروفون محظور",
          {
            description: lang === "en"
              ? "Enable it in Settings → Calofay → Microphone"
              : "فعّله من الإعدادات ← كالوفاي ← الميكروفون",
            duration: 8000,
            action: {
              label: lang === "en" ? "Open Settings" : "افتح الإعدادات",
              onClick: () => openAppSettings(),
            },
          },
        );
      } else {
        toast.error(lang === "en" ? "Couldn't access microphone" : "تعذر الوصول للميكروفون");
      }
    }
  };
  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    try { mediaRecorderRef.current?.stop(); } catch {}
  };

  // Edit-mode support: when the URL carries `?edit=<meal_id>&date=<YYYY-MM-DD>`,
  // preload the existing meal into the review form so the user can amend
  // anything (name, ingredients, meal type, notes) using the same input
  // surface they created it with — then hit "حفظ التعديلات" to PATCH.
  // We fetch via GET /meals?date=X (no dedicated GET /meals/{id} needed)
  // and match by id. `editMeal` stays truthy for the whole session so
  // `save` knows to PATCH instead of POST.
  const editId = params.get("edit");
  const recipeEditId = params.get("recipe_edit");
  useEffect(() => {
    if (!recipeEditId) return;
    (async () => {
      try {
        const [{ data: rec }, { data: br }] = await Promise.all([
          api.get(`/user-recipes/${recipeEditId}`),
          api.get(`/meals/recipe-items/${recipeEditId}`, { params: { lang } }),
        ]);
        const r = rec.recipe || rec;
        const sv = Math.max(1, Number(r.servings) || 1);
        setMealName(r.title || "");
        setItems((br.items || []).map((i) => ({
          ...i, quantity_g: Math.round(i.quantity_g * sv), calories: Math.round(i.calories * sv),
          protein_g: Math.round(i.protein_g * sv * 10) / 10, carbs_g: Math.round(i.carbs_g * sv * 10) / 10,
          fat_g: Math.round(i.fat_g * sv * 10) / 10,
        })));
        setNotes((r.steps || []).map((x) => (typeof x === "string" ? x : x?.text || "")).filter(Boolean).join("\n"));
        setMode("review");
      } catch { toast.error(lang === "en" ? "Couldn't load recipe" : "تعذّر تحميل الوصفة"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeEditId]);
  const [editMeal, setEditMeal] = useState(null);

  useEffect(() => {
    if (!editId) return;
    const date = params.get("date") || todayISO();
    (async () => {
      try {
        const { data } = await api.get(`/meals?date=${date}`);
        const meal = (data || []).find((mm) => mm.id === editId);
        if (!meal) {
          toast.error(lang === "en" ? "Meal not found" : "الوجبة غير موجودة");
          nav("/app", { replace: true });
          return;
        }
        setEditMeal(meal);
        setMealType(meal.meal_type || "lunch");
        setMealName(meal.name || "");
        setItems(await expandRecipeMeal(meal, lang));
        setNotes(meal.notes || "");
        setMode("review");
      } catch {
        toast.error(lang === "en" ? "Couldn't load meal" : "تعذر تحميل الوجبة");
        nav("/app", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Deep-link required from Dashboard: /app/add?type=breakfast&mode=photo|describe|manual|search|barcode
  useEffect(() => {
    // Edit mode is handled by the effect above — bail so we don't
    // clobber the pre-loaded state or redirect home.
    if (editId || recipeEditId) return;
    const t = params.get("type");
    const m = params.get("mode");
    if (t && ["breakfast","lunch","dinner","snack"].includes(t)) setMealType(t);
    // Bug fix (Feb 26 2026): previously we auto-invoked the camera the
    // moment the screen mounted with `?mode=photo` — great when the only
    // option WAS the camera, but confusing now that users also have the
    // "Choose from gallery" alternative. Auto-clicking bypasses that
    // choice and pins them to the camera. Fix: land on the picker card
    // instead, so users decide (camera vs library) themselves.
    if (m === "photo") setMode("photo");
    else if (m === "describe") setMode("describe");
    else if (m === "manual")   {
      // Multi-item manual entry (like recipe editor)
      setItems([{ name: "", quantity_g: 0, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }]);
      setMealName("");
      setMode("review");
    }
    else if (m === "barcode")  { setMode("barcode_scanning"); setScannerOpen(true); }
    else if (m === "label")    setMode("label");
    else {
      // No mode → go home (choose page removed; user picks from Dashboard bottom sheet)
      nav("/app", { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const foodToItem = (food, servings = 1) => {
    const g = (food.default_serving_g || 100) * servings;
    const factor = g / 100;
    const p100 = food.per_100g || {};
    return {
      name: food.name_ar,
      quantity_g: g,
      // Beverages / liquids scanned via barcode (or flagged by AI) start on
      // "مل" so the review UI matches what's on the label (e.g. a 200 ml
      // milk bottle shows "200 مل", not "200 غ"). Users can still toggle.
      unit: food.is_liquid ? "ml" : "g",
      calories: Math.round((p100.calories || 0) * factor),
      protein_g: Math.round((p100.protein_g || 0) * factor * 10) / 10,
      carbs_g: Math.round((p100.carbs_g || 0) * factor * 10) / 10,
      fat_g: Math.round((p100.fat_g || 0) * factor * 10) / 10,
      // Keep the per-100g reference so editing the quantity later auto-scales the macros
      per_100g: {
        calories: Number(p100.calories) || 0,
        protein_g: Number(p100.protein_g) || 0,
        carbs_g: Number(p100.carbs_g) || 0,
        fat_g: Number(p100.fat_g) || 0,
      },
    };
  };

  const addFoodItem = (food) => {
    const it = foodToItem(food, 1);
    setItems((prev) => [...prev, it]);
    if (!mealName) setMealName(food.name_ar);
    toast.success(lang === "en" ? "Added" : "تمت الإضافة");
    setMode("review");
  };

  const handleBatchDone = (results) => {
    setScannerOpen(false);
    const en = lang === "en";
    const unknown = en ? "Unknown product" : "منتج غير معروف";
    const rows = results.map((r) => {
      if (!r.food) {
        return { name: `${unknown} · ${r.code}`, quantity_g: 0, unit: "g", calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0,
          _pending_label: { barcode: r.code, isLiquid: false, suggestedName: "", missing: true } };
      }
      const it = foodToItem(r.food, 1);
      return r.food.needs_contribution
        ? { ...it, _pending_label: { barcode: r.code, isLiquid: !!r.food.is_liquid, suggestedName: r.food.name_ar || "" } }
        : it;
    });
    setItems((prev) => [...prev, ...rows]);
    const named = results.find((r) => r.food);
    if (!mealName && named) setMealName(named.food.name_ar);
    setMode("review");
    const pending = rows.filter((x) => x._pending_label).length;
    toast.success(en ? `${rows.length} product${rows.length === 1 ? "" : "s"} added` : `تمت إضافة ${rows.length} منتج`, {
      description: pending ? (en ? `${pending} need a label photo — tap "Snap label" on each` : `${pending} تحتاج صورة الملصق — اضغط «صوّر الملصق» عليها`) : undefined,
    });
  };

  const openLabelFor = (idx) => {
    const p = items[idx]?._pending_label;
    if (p) setContributor({ barcode: p.barcode, suggestedName: p.suggestedName, isLiquid: p.isLiquid, itemIndex: idx });
  };

  const handleBarcodeDetected = async (code, prefetchedFood = null) => {
    setScannerOpen(false);
    // The scanner's AI-OCR fallback may already have resolved the product
    // via OpenFoodFacts in the same round-trip. If it did, use that payload
    // directly and skip the extra `/foods/barcode/{code}` request.
    if (prefetchedFood) {
      addFoodItem(prefetchedFood);
      maybePromptContribution(prefetchedFood, code, items.length);
      return;
    }
    // Always fetch fresh from OpenFoodFacts — no local cache. Users complained
    // that a stale cached entry could shadow updated packaging data, and the
    // extra ~200 ms lookup is imperceptible compared to the value of always
    // showing the current nutrition label for that barcode.
    try {
      const { data } = await api.get(`/foods/barcode/${code}`);
      addFoodItem(data.food);
      maybePromptContribution(data.food, code, items.length);
    } catch (e) {
      // 404 = product missing from OpenFoodFacts. Prompt the user to snap
      // the nutrition label so we can read the numbers for them — no need
      // for them to give up and log the meal blind.
      if (e?.response?.status === 404) {
        toast.info(
          lang === "en"
            ? "Product not found — snap the nutrition label and we'll read it"
            : "لم نجد هذا المنتج — التقط صورة للملصق وسنقرأه لك",
        );
        setContributor({
          barcode: code,
          suggestedName: "",
          isLiquid: false,
          itemIndex: null, // fresh add, no existing item to replace
        });
        return;
      }
      toast.error(lang === "en" ? "Product not found — add it manually" : "لم يتم العثور على المنتج — أدخله يدوياً");
      setItems([{ name: "", quantity_g: 0, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }]);
      setMealName("");
      setMode("review");
    }
  };

  // When a scanned product has incomplete OFF data (calories==0 or macros all
  // zero), open the label reader immediately so the user can either snap the
  // label or hit the prominent "Skip & enter manually" button inside the
  // sheet. The banner in the review UI stays as a re-entry point in case the
  // user closes the sheet and changes their mind.
  const maybePromptContribution = (food, code, itemIndex) => {
    if (food && food.needs_contribution) {
      setContributor({
        barcode: code,
        suggestedName: food.name_ar || "",
        isLiquid: !!food.is_liquid,
        itemIndex,
      });
    }
  };

  // Called when the user finishes the "Complete this product" flow. Swap in
  // the fresh contributed food data at the corresponding item index so the
  // review UI reflects real macros immediately.
  const handleContributed = (freshFood) => {
    if (!contributor) { setContributor(null); return; }
    const idx = contributor.itemIndex;
    if (idx == null) {
      // Fresh add (barcode wasn't in OFF at all) → append the new item.
      addFoodItem(freshFood);
    } else {
      // Replace the placeholder OFF item with the fully-populated one.
      // Label had no pack/serving size → keep the quantity the row already had.
      setItems((prev) => prev.map((x, i) => {
        if (i !== idx) return x;
        const keep = freshFood.serving_source === "none" && Number(x.quantity_g) > 0;
        return foodToItem(keep ? { ...freshFood, default_serving_g: Number(x.quantity_g) } : freshFood, 1);
      }));
      if (!mealName) setMealName(freshFood.name_ar || "");
    }
    setContributor(null);
  };

  // Derive a per-100g reference from any AI/manual item so editing the quantity
  // in the review screen auto-recalculates calories + macros proportionally.
  const withPer100 = (rawItems) => (rawItems || []).map((it) => {
    const q = Number(it.quantity_g) || 100;
    if (q <= 0) return it;
    const k = 100 / q;
    return {
      ...it,
      quantity_g: q,
      // Beverages the AI flagged as liquid get their unit toggle pre-set
      // to "مل" so the review UI reads naturally (200 مل، not 200 غ).
      unit: it.is_liquid ? "ml" : (it.unit || "g"),
      per_100g: {
        calories:  Math.round((Number(it.calories)  || 0) * k),
        protein_g: Math.round((Number(it.protein_g) || 0) * k * 10) / 10,
        carbs_g:   Math.round((Number(it.carbs_g)   || 0) * k * 10) / 10,
        fat_g:     Math.round((Number(it.fat_g)     || 0) * k * 10) / 10,
      },
    };
  });

  const analyzeDescription = async () => {
    if (!description.trim()) return toast.error(lang === "en" ? "Please describe the meal" : "اكتب وصف الوجبة");
    // Apple 5.1.1 / 5.1.2 — explicit consent before describe → OpenAI.
    const ok = await ensureAIConsent(lang);
    if (!ok) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/meals/analyze/describe", { description, language: lang });
      if (!(data.items || []).length) {
        toast.error(lang === "en" ? "Couldn't understand the meal — describe it more clearly" : "ما فهمت الوجبة — اكتب وصفاً أوضح");
        return;
      }
      setItems(withPer100(data.items));
      setMealName(data.meal_name || description.slice(0, 40));
      setConfidence(data.confidence || 0.5);
      if (data.notes) setNotes(data.notes);
      setMode("review");
    } catch (e) {
      toast.error(lang === "en" ? "Couldn't analyze meal, try again" : "تعذر تحليل الوجبة، حاول مرة أخرى");
    } finally {
      setAnalyzing(false);
    }
  };

  // "Scan Nutrition Label" flow — user taps the new Dashboard picker option
  // and lands here in `mode === "label"`. We open the camera, ship the image
  // to `/foods/nutrition-ocr` (GPT-4o Vision), then materialize a single
  // review item with the per-100g reference so editing the quantity in the
  // review UI auto-scales calories + macros (identical UX to a barcode scan).
  const analyzeLabel = async (file) => {
    // Apple 5.1.1 / 5.1.2 — explicit consent before label OCR → OpenAI Vision.
    const ok = await ensureAIConsent(lang);
    if (!ok) return;
    setAnalyzing(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = () => rej(new Error("read fail"));
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/foods/nutrition-ocr", { image_base64: b64 });
      const p100 = data.per_100g || {};
      if ((Number(p100.calories) || 0) <= 0) {
        toast.error(lang === "en"
          ? "Couldn't read calories from the label — try a clearer photo"
          : "تعذّر قراءة السعرات من الملصق — التقط صورة أوضح");
        setAnalyzing(false);
        // Reopen the picker so the user can retry without extra taps.
        setTimeout(() => fileRef.current?.click(), 150);
        return;
      }
      // Default serving = the label's stated size (if any), otherwise 100.
      const servingG = Number(data.serving_size_g) || 100;
      const factor = servingG / 100;
      const nameAr = data.product_name_ar || data.product_name_en || (lang === "en" ? "Scanned product" : "منتج ممسوح");
      const item = {
        name: nameAr,
        quantity_g: servingG,
        unit: data.is_liquid ? "ml" : "g",
        calories:  Math.round((Number(p100.calories)  || 0) * factor),
        protein_g: Math.round((Number(p100.protein_g) || 0) * factor * 10) / 10,
        carbs_g:   Math.round((Number(p100.carbs_g)   || 0) * factor * 10) / 10,
        fat_g:     Math.round((Number(p100.fat_g)     || 0) * factor * 10) / 10,
        per_100g: {
          calories:  Number(p100.calories)  || 0,
          protein_g: Number(p100.protein_g) || 0,
          carbs_g:   Number(p100.carbs_g)   || 0,
          fat_g:     Number(p100.fat_g)     || 0,
        },
      };
      setItems([item]);
      setMealName(nameAr);
      setConfidence(Number(data.confidence) || 0.7);
      if (data.notes) setNotes(data.notes);
      setPrevMode("label");
      setMode("review");
      const ok = (Number(data.confidence) || 0) >= 0.6;
      if (ok) toast.success(lang === "en" ? "Label read ✓ — adjust the amount if needed" : "تمت قراءة الملصق ✓ — عدّل الكمية لو تحتاج");
      else    toast.warning(lang === "en" ? "AI wasn't sure — double-check the values" : "الذكاء الاصطناعي غير متأكد — راجع الأرقام");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(detail || (lang === "en" ? "Daily AI limit reached" : "وصلت للحد اليومي"));
      } else {
        toast.error(lang === "en" ? "Couldn't read the label" : "تعذّرت قراءة الملصق");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzePhoto = async (file) => {
    // Apple 5.1.1 / 5.1.2 — explicit consent before photo → OpenAI Vision.
    const ok = await ensureAIConsent(lang);
    if (!ok) return;
    setAnalyzing(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = reader.result.split(",")[1];
        try {
          const { data } = await api.post("/meals/analyze/photo", { image_base64: b64, language: lang });
          setItems(withPer100(data.items));
          setMealName(data.meal_name || (lang === "en" ? "Meal" : "وجبة"));
          setConfidence(data.confidence || 0.5);
          if (data.notes) setNotes(data.notes);
          setPrevMode("photo");
          setMode("review");
        } catch (e) {
          toast.error(lang === "en" ? "Couldn't analyze image" : "تعذر تحليل الصورة");
        } finally {
          setAnalyzing(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      setAnalyzing(false);
      toast.error(lang === "en" ? "Couldn't read image" : "تعذرت قراءة الصورة");
    }
  };

  const totals = items.reduce((acc, it) => ({
    calories: acc.calories + (Number(it.calories) || 0),
    protein: acc.protein + (Number(it.protein_g) || 0),
    carbs: acc.carbs + (Number(it.carbs_g) || 0),
    fat: acc.fat + (Number(it.fat_g) || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const validate = () => {
    if (!mealName.trim()) { toast.error(lang === "en" ? "Enter a meal name first" : "اكتب اسم الوجبة أولاً"); return false; }
    if (items.length === 0) { toast.error(lang === "en" ? "Add at least one ingredient" : "أضف مكوّن واحد على الأقل"); return false; }
    const bad = items.findIndex((it) => !String(it.name || "").trim());
    if (bad !== -1) { toast.error(`اكتب اسم المكوّن رقم ${bad + 1}`); return false; }
    const unlabeled = items.filter((it) => it._pending_label && !(Number(it.calories) > 0)).length;
    if (unlabeled) {
      toast.error(lang === "en"
        ? `${unlabeled} product${unlabeled === 1 ? " has" : "s have"} no nutrition yet — snap the label or remove ${unlabeled === 1 ? "it" : "them"}`
        : `${unlabeled} منتج بدون قيم غذائية بعد — صوّر الملصق أو احذفه`);
      return false;
    }
    const totalCal = items.reduce((s, it) => s + (Number(it.calories) || 0), 0);
    if (totalCal <= 0) { toast.error(lang === "en" ? "Enter calories for at least one ingredient" : "أدخل السعرات الحرارية لمكوّن واحد على الأقل"); return false; }
    return true;
  };

  const buildMealPayload = () => ({
    // Save under whichever date the user was viewing when they tapped "add".
    // The Dashboard forwards `?date=YYYY-MM-DD` when the user is browsing a
    // past day so all four entry modes (manual / barcode / photo / describe)
    // land on the correct day. Fallback = today for the normal live case.
    date: (params.get("date") || todayISO()),
    meal_type: mealType,
    name: mealName.trim(),
    items: items.map((it) => ({
      // Spread FIRST so per_100g, unit, and any future MealItem field
      // survive the round-trip (critical for barcode/label items whose
      // auto-scale feature relies on per_100g). Pydantic drops any
      // unknown extras silently on the backend so this is safe.
      ...it,
      name: it.name,
      quantity_g: Number(it.quantity_g) || 100,
      calories: Number(it.calories) || 0,
      protein_g: Number(it.protein_g) || 0,
      carbs_g: Number(it.carbs_g) || 0,
      fat_g: Number(it.fat_g) || 0,
      assumption: it.assumption || null,
    })),
    source: mode === "photo" ? "photo" : mode === "describe" ? "describe" : "manual",
    notes: splitPoints(notes).join("\n") || null,
  });

  const _backToHome = () => {
    // Preserve the past-day context so the user lands back on the same day
    // they were viewing (Dashboard reads `?date=` from the URL).
    const d = params.get("date");
    nav(d ? `/app?date=${d}` : "/app");
  };

  // "Enjoying Calofay?" 5-star review sheet. Shown after a successful
  // meal save if the user has crossed the activity threshold (3+ meals,
  // ≥ 1 day since install) and isn't in cooldown. See /lib/reviewPrompt.
  const [reviewOpen, setReviewOpen] = useState(false);

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (recipeEditId) {
        await api.patch(`/user-recipes/${recipeEditId}`, {
          title: mealName.trim(),
          steps: splitPoints(notes),
          items: items.filter((i) => (i.name || "").trim()).map((i) => ({
            name: i.name.trim(), quantity_g: Number(i.quantity_g) || 0, unit: i.unit || "g",
            calories: Number(i.calories) || 0, protein_g: Number(i.protein_g) || 0,
            carbs_g: Number(i.carbs_g) || 0, fat_g: Number(i.fat_g) || 0,
          })),
        });
        toast.success(lang === "en" ? "Recipe updated" : "تم حفظ التعديلات");
        nav(`/app/imported/${recipeEditId}`, { replace: true });
        return;
      }
      const payload = buildMealPayload();
      if (editId && editMeal) {
        // PATCH mode — only send fields the update endpoint accepts.
        // meal_type is now supported server-side so users can also
        // re-bucket a meal (breakfast → lunch, etc.) from here.
        await api.patch(`/meals/${editId}`, {
          name: payload.name,
          meal_type: payload.meal_type,
          items: payload.items,
          notes: payload.notes,
        });
        invalidateSWR(`notes:${payload.date}`);
        toast.success(lang === "en" ? "Meal updated" : "تم حفظ التعديلات");
        _backToHome();
        return;
      }
      await api.post("/meals", payload);
      // Backend auto-populated the DayNotes ingredients for this meal; drop
      // the stale cached notes doc so the dashboard re-fetches on remount.
      invalidateSWR(`notes:${payload.date}`);
      toast.success(lang === "en" ? "Meal saved" : "تم حفظ الوجبة");
      // Track activity for the review-prompt gating logic, then decide
      // whether to open the sheet BEFORE navigating home so the modal
      // renders on the review page (not blank home).
      noteMealSaved();
      if (shouldPromptForReview()) {
        setReviewOpen(true);
      } else {
        _backToHome();
      }
    } catch { toast.error(lang === "en" ? "Save failed" : "تعذر الحفظ"); }
    finally { setSaving(false); }
  };

  const saveMealAndRecipe = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildMealPayload();
      // 1) Log as meal for today
      await api.post("/meals", payload);
      invalidateSWR(`notes:${payload.date}`);
      // 2) Also promote to a reusable personal recipe (stored alongside
      //    AI-imported recipes so the trainee sees a single unified list).
      await api.post("/user-recipes/from-meal", {
        name: mealName.trim(),
        servings: 1,
        notes: splitPoints(notes).join("\n") || null,
        items: items.map((it) => ({
          name: it.name,
          quantity_g: Number(it.quantity_g) || 100,
          unit: it.unit || "g",
          calories: Number(it.calories) || 0,
          protein_g: Number(it.protein_g) || 0,
          carbs_g: Number(it.carbs_g) || 0,
          fat_g: Number(it.fat_g) || 0,
        })),
      });
      toast.success(lang === "en" ? "Meal added & saved to favorites ✨" : "تمت إضافة الوجبة وحُفظت في مفضلاتي ✨");
      _backToHome();
    } catch { toast.error(lang === "en" ? "Save failed" : "تعذر الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="pb-8 min-h-screen">
      <header className="sticky top-0 bg-white/95 backdrop-blur z-10 border-b border-neutral-100 px-5 h-14 flex items-center gap-3">
        <button
          onClick={() => {
            // Context-aware back: if we're on the review screen and the
            // user got here from photo/label picker, rewind to that
            // picker (with items cleared) so they can retake without
            // re-entering the meal-type flow. Any other state → exit
            // straight to Home like before.
            if (mode === "review" && prevMode && ["photo", "label"].includes(prevMode)) {
              setItems([]);
              setConfidence(1);
              setMode(prevMode);
              return;
            }
            if (recipeEditId) { nav(-1); return; }
            _backToHome();
          }}
          className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center"
          data-testid="add-meal-back"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="font-semibold flex-1 text-right">{recipeEditId ? (lang === "en" ? "Edit recipe" : "تعديل الوصفة") : editId ? (lang === "en" ? "Edit meal" : "تعديل الوجبة") : (lang === "en" ? "Add Meal" : "إضافة وجبة")}</div>
      </header>

      {mode === "barcode_scanning" && !scannerOpen && (
        <div className="p-10 text-center">
          <Spinner size="md" bare className="mx-auto mb-3" />
          <div className="text-sm text-neutral-500">{lang === "en" ? "Opening scanner..." : "جارٍ فتح الماسح..."}</div>
        </div>
      )}

      {scannerOpen && (
        <BarcodeScanner
          onDetected={handleBarcodeDetected}
          onDone={handleBatchDone}
          onClose={() => {
            setScannerOpen(false);
            // If user cancelled from the barcode-only flow (no items yet), go home (preserve past-day)
            if (mode === "barcode_scanning" && items.length === 0) _backToHome();
          }}
        />
      )}

      {contributor && !contributor.deferred && (
        <NutritionLabelContributor
          barcode={contributor.barcode}
          suggestedName={contributor.suggestedName}
          isLiquid={contributor.isLiquid}
          onContributed={handleContributed}
          onClose={() => setContributor((c) => (!c || items[c.itemIndex]?._pending_label ? null : { ...c, deferred: true }))}
          onSkipManual={() => setContributor(null)}
        />
      )}

      <ReviewPromptModal
        open={reviewOpen}
        onClose={() => { setReviewOpen(false); _backToHome(); }}
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (mode === "label") analyzeLabel(f);
          else                  analyzePhoto(f);
        }}
        // `onCancel` fires when the user backs out of the native file
        // picker on browsers that support the HTML spec's cancel event
        // (Chromium 113+, Safari 17+). On older engines we rely on the
        // fallback "no picture yet" UI below to give the user a way out.
        onCancel={() => { if ((mode === "photo" || mode === "label") && items.length === 0) _backToHome(); }}
        data-testid="meal-photo-input"
      />

      {/* Gallery picker — identical to `fileRef` above but WITHOUT the
          `capture="environment"` attribute. That single omission is what
          tells iOS Safari / Android Chrome (and the Capacitor WebView on
          both) to present the native photo library instead of jumping to
          the camera. Works on: iOS 14+ (Photos), Android 5+ (Gallery), all
          modern desktop browsers. No native plugin required. */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (mode === "label") analyzeLabel(f);
          else                  analyzePhoto(f);
        }}
        onCancel={() => { if ((mode === "photo" || mode === "label") && items.length === 0) _backToHome(); }}
        data-testid="meal-gallery-input"
      />

      {/* Fallback UI when photo mode is active but no picture has been
          captured yet (user cancelled the camera, or their browser
          doesn't fire `onCancel`). Without this, users would land on
          a blank page with no way back. */}
      {mode === "photo" && !analyzing && items.length === 0 && (
        <div className="p-6 space-y-4">
          <div className="bg-white rounded-3xl border-[1.5px] border-black p-6 text-center">
            <div className="w-14 h-14 rounded-full orange-gradient text-white flex items-center justify-center mx-auto mb-3 shadow-brand">
              <Camera className="w-6 h-6" />
            </div>
            <div className="font-black text-lg mb-1">{lang === "en" ? "Photo your meal" : "صوّر وجبتك"}</div>
            <p className="text-sm text-neutral-500 mb-5 leading-relaxed">
              {lang === "en" ? "Point the camera at your plate — AI will estimate calories and macros for you." : "وجّه الكاميرا نحو الطبق والذكاء الاصطناعي راح يحسبلك السعرات والماكرو تلقائياً."}
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="retake-photo-btn"
            >
              <Camera className="w-5 h-5" /> {lang === "en" ? "Open camera" : "افتح الكاميرا"}
            </button>
            {/* Secondary "Choose from gallery" CTA. Indigo/blue gradient to
                sit visually second to the primary orange camera button but
                still feel premium — never a muted grey, since users pick
                between camera vs library equally often (import old meal
                photos, screenshots of restaurant menus, etc). */}
            <button
              onClick={() => galleryRef.current?.click()}
              className="w-full mt-3 bg-gradient-to-r from-indigo-500 to-blue-500 text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="pick-from-gallery-btn"
            >
              <ImageIcon className="w-5 h-5" /> {lang === "en" ? "Choose from gallery" : "اختر من الاستوديو"}
            </button>
            <button
              onClick={_backToHome}
              className="w-full mt-3 bg-neutral-100 text-neutral-700 font-semibold py-3 rounded-full active:scale-[0.98]"
              data-testid="cancel-photo-btn"
            >
              {lang === "en" ? "Back" : "رجوع"}
            </button>
          </div>
        </div>
      )}

      {mode === "describe" && (
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs text-neutral-500 font-medium">{lang === "en" ? "Describe your meal" : "صف وجبتك"}</div>
              <div className="text-[10px] text-neutral-400">{lang === "en" ? "Type or tap the mic and speak" : "اكتب أو اضغط الميكروفون وتحدّث"}</div>
            </div>
            <div className="relative">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                placeholder={recording ? (lang === "en" ? "🎙️ Recording — speak now..." : "🎙️ جارٍ التسجيل — تحدّث الآن...") : (transcribing ? (lang === "en" ? "Transcribing..." : "جارٍ تحويل الصوت إلى نص...") : (lang === "en" ? "e.g. I ate 10 spoons of rice and half a grilled chicken" : "مثال: أكلت 10 ملاعق رز ونصف دجاجة مشوية"))}
                disabled={recording || transcribing}
                className="w-full bg-neutral-50 border border-neutral-100 rounded-2xl px-5 py-4 pl-14 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 disabled:opacity-70"
                dir="rtl"
                data-testid="describe-input"
              />
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                className={`absolute bottom-3 left-3 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-60 ${
                  recording
                    ? "bg-red-500 text-white animate-pulse shadow-brand"
                    : "bg-brand-500 text-white hover:bg-brand-600 shadow-brand"
                }`}
                data-testid="describe-mic-btn"
                aria-label={recording ? (lang === "en" ? "Stop recording" : "إيقاف التسجيل") : (lang === "en" ? "Voice input" : "تسجيل صوتي")}
                title={recording ? (lang === "en" ? "Stop recording" : "إيقاف التسجيل") : (lang === "en" ? "Voice input" : "تسجيل صوتي")}
              >
                {transcribing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : recording ? (
                  <Square className="w-4 h-4" fill="currentColor" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <button
            onClick={analyzeDescription}
            disabled={analyzing || !description.trim() || recording || transcribing}
            className="w-full orange-gradient text-white font-semibold py-4 rounded-full shadow-floating active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="analyze-describe-btn"
          >
            {analyzing ? <><Loader2 className="w-5 h-5 animate-spin" /> {lang === "en" ? "Analyzing..." : "جاري التحليل..."}</> : (lang === "en" ? "Analyze meal" : "حلل الوجبة")}
          </button>
        </div>
      )}

      {mode === "photo" && analyzing && (
        <div className="p-10 text-center">
          <Spinner size="lg" bare className="mx-auto mb-3" />
          <div>{lang === "en" ? "Analyzing image..." : "جاري تحليل الصورة..."}</div>
        </div>
      )}

      {/* Nutrition-label OCR mode — same UX shape as `mode === "photo"`:
          shows a loader while GPT-4o Vision reads the label, then flips
          straight to the shared review UI. If the user cancels the OS
          camera before capturing, the fallback CTA below gives them a
          way back in (mirrors the retake-photo behaviour). */}
      {mode === "label" && analyzing && (
        <div className="p-10 text-center">
          <Spinner size="lg" bare className="mx-auto mb-3" />
          <div className="font-semibold">{lang === "en" ? "Reading the label..." : "جارٍ قراءة الملصق..."}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {lang === "en" ? "Extracting calories and macros" : "نستخرج السعرات والماكرو"}
          </div>
        </div>
      )}

      {mode === "label" && !analyzing && items.length === 0 && (
        <div className="p-6 space-y-4">
          <div className="bg-white rounded-3xl border-[1.5px] border-black p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-fuchsia-500 via-purple-500 to-indigo-500 text-white flex items-center justify-center mx-auto mb-3 shadow-brand">
              <ScanLine className="w-6 h-6" />
            </div>
            <div className="font-black text-lg mb-1">{lang === "en" ? "Scan a nutrition label" : "صوّر ملصق التغذية"}</div>
            <p className="text-sm text-neutral-500 mb-5 leading-relaxed">
              {lang === "en"
                ? "Point the camera at the label and we'll read the calories & macros in seconds."
                : "وجّه الكاميرا نحو الملصق وسنقرأ السعرات والماكرو في ثوانٍ."}
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="retake-label-btn"
            >
              <Camera className="w-5 h-5" /> {lang === "en" ? "Open camera" : "افتح الكاميرا"}
            </button>
            {/* Gallery picker for label mode — mirrors the meal-photo
                flow. Same indigo/blue gradient so users learn the pattern:
                orange = camera, blue = library, grey = cancel. */}
            <button
              onClick={() => galleryRef.current?.click()}
              className="w-full mt-3 bg-gradient-to-r from-indigo-500 to-blue-500 text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="pick-label-from-gallery-btn"
            >
              <ImageIcon className="w-5 h-5" /> {lang === "en" ? "Choose from gallery" : "اختر من الاستوديو"}
            </button>
            <button
              onClick={_backToHome}
              className="w-full mt-3 bg-neutral-100 text-neutral-700 font-semibold py-3 rounded-full active:scale-[0.98]"
              data-testid="cancel-label-btn"
            >
              {lang === "en" ? "Back" : "رجوع"}
            </button>
          </div>
        </div>
      )}

      {mode === "review" && (
        <div className="p-5 space-y-4">
          {confidence < 0.6 && (
            <div className="bg-amber-50 border border-amber-100 text-amber-800 text-sm rounded-2xl p-3">
              {lang === "en" ? "Low-confidence analysis — please review ingredients & amounts before saving." : "دقة التحليل منخفضة — راجع المكونات والأحجام قبل الحفظ."}
            </div>
          )}
          {contributor?.deferred && (
            <button
              onClick={() => setContributor((c) => c ? { ...c, deferred: false } : c)}
              className={`w-full bg-gradient-to-l from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4 active:scale-[0.99] transition ${lang === "en" ? "text-left" : "text-right"}`}
              data-testid="complete-product-banner"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full orange-gradient text-white flex items-center justify-center flex-shrink-0 shadow-brand">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-orange-900">
                    {lang === "en" ? "We couldn't find full data for this product" : "لم نجد بيانات كافية لهذا المنتج"}
                  </div>
                  <div className="text-xs text-orange-800/80 mt-0.5 leading-relaxed">
                    {lang === "en"
                      ? "Snap the nutrition label — we'll read the numbers for you."
                      : "التقط صورة الملصق الغذائي وسنقرأ القيم من الصورة."}
                  </div>
                </div>
              </div>
            </button>
          )}
          <Field label={lang === "en" ? "Meal name" : "اسم الوجبة"} value={mealName} onChange={setMealName} testid="review-name" />

          <div className="bg-white border border-neutral-100 rounded-3xl p-4 shadow-card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm">{lang === "en" ? "Ingredients" : "المكونات"}</div>
              <button onClick={() => setItems([...items, { name: "", quantity_g: 0, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }])} className="text-xs text-brand-500 font-medium flex items-center gap-1" data-testid="add-item-btn">
                <Plus className="w-3.5 h-3.5" /> {lang === "en" ? "Add ingredient" : "إضافة مكوّن"}
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <ItemRow
                  key={idx} item={it}
                  below={it._pending_label ? <PendingLabelBar en={lang === "en"} missing={it._pending_label.missing} onSnap={() => openLabelFor(idx)} idx={idx} /> : null}
                  onChange={(patch) => setItems(items.map((x, i) => i === idx ? { ...x, ...patch } : x))}
                  onRemove={() => setItems(items.filter((_, i) => i !== idx))}
                  idx={idx}
                />
              ))}
              {items.length === 0 && (
                <div className="text-center py-4 text-neutral-400 text-sm">{lang === "en" ? "No ingredients — add one" : "لا توجد مكونات — أضف مكوناً"}</div>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-neutral-500 mb-1.5 pr-2" dir="rtl">{recipeEditId ? (lang === "en" ? "📝 Notes / steps" : "📝 الملاحظات / الخطوات") : (lang === "en" ? "📝 Meal notes" : "📝 ملاحظات الوجبة")}</div>
            <div className="bg-white shadow-card rounded-2xl p-3">
              <NotesPoints value={notes} onChange={setNotes} en={lang === "en"} testid="review-notes" />
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-neutral-500 mb-1.5 pr-2" dir="rtl">{lang === "en" ? "📊 Meal totals" : "📊 إجمالي الوجبة"}</div>
            <div className="bg-brand-50 shadow-card rounded-2xl p-4 grid grid-cols-4 gap-2 text-center" data-testid="review-totals">
              <TotalCol label={lang === "en" ? "cal" : "سعرات"} v={totals.calories} />
              <TotalCol label={lang === "en" ? "protein" : "بروتين"} v={totals.protein} suffix={lang === "en" ? "g" : "غ"} />
              <TotalCol label={lang === "en" ? "carbs" : "كارب"} v={totals.carbs} suffix={lang === "en" ? "g" : "غ"} />
              <TotalCol label={lang === "en" ? "fat" : "دهون"} v={totals.fat} suffix={lang === "en" ? "g" : "غ"} />
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={save}
              disabled={saving}
              className="w-full orange-gradient text-white font-semibold py-4 rounded-full shadow-floating active:scale-[0.98] disabled:opacity-60"
              data-testid="save-meal-btn"
            >
              {saving
                ? (lang === "en" ? "Saving..." : "جاري الحفظ...")
                : editId || recipeEditId
                  ? (lang === "en" ? "Save changes" : "حفظ التعديلات")
                  : (lang === "en" ? "Log meal" : "أدخل الوجبة")}
            </button>
            {/* "Log & save to favorites" only shown when creating a new
                meal — during edit the meal already exists so this button
                would produce a duplicate recipe. */}
            {!editId && !recipeEditId && (
              <button
                onClick={saveMealAndRecipe}
                disabled={saving}
                className="w-full bg-white border-2 border-brand-500 text-brand-600 font-semibold py-3.5 rounded-full active:scale-[0.98] disabled:opacity-60"
                data-testid="save-recipe-btn"
              >
                {saving ? "..." : (lang === "en" ? "Log meal & save to favorites" : "أدخل الوجبة واحفظ في مفضلاتي")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <div className="text-xs text-neutral-500 mb-1.5 font-medium">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white shadow-card rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500/40 transition-colors" data-testid={testid} />
    </div>
  );
}

function PendingLabelBar({ en, missing, onSnap, idx }) {
  const msg = missing
    ? (en ? "Not in our database — snap the label showing calories & macros" : "غير موجود في قاعدة البيانات — صوّر الملصق الذي عليه السعرات والماكروز")
    : (en ? "Incomplete data — snap the label showing calories & macros" : "بياناته ناقصة — صوّر الملصق الذي عليه السعرات والماكروز");
  return (
    <button onClick={onSnap} data-testid={`snap-label-btn-${idx}`}
      className="w-full mb-2 flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-900 rounded-xl px-3 py-2 text-[11px] font-semibold leading-snug active:scale-[0.99] transition">
      <Camera className="w-4 h-4 text-orange-600 shrink-0" />
      <span className="flex-1 text-start">{msg}</span>
      <span className="orange-gradient text-white rounded-full px-3 py-1 shrink-0">{en ? "Snap label" : "صوّر الملصق"}</span>
    </button>
  );
}
